/**
 * Conversation outcome — the read/write contract for the AI-containment funnel.
 *
 * A conversation's terminal outcome is persisted as a universal Score:
 * `name = CONVERSATION_OUTCOME_SCORE_NAME`, `valueType = CATEGORICAL`,
 * `emitterType = SYSTEM_EVENT`, `scorableType = CHAT`, the outcome itself in
 * `categoricalValue`. The backend classifier writes those rows; the analytics
 * containment-funnel widget reads them back as a `count` over
 * `analytics_score_events` grouped by `categoricalValue`.
 *
 * **Why this lives in `@nexus/types` rather than beside the classifier.** The
 * score name is a contract spanning the write side and the read side, and the
 * read side is the frontend. Held only on the backend, the widget would have to
 * hard-code the literal — and analytics filters compile to inline SQL literals,
 * so a backend rename would not fail to compile and would not throw at runtime.
 * It would simply return no rows, and the funnel would render an empty chart
 * that reads as "no conversations". One definition, imported by both sides, is
 * what makes that failure impossible rather than merely unlikely.
 */

/** `Score.name` under which every conversation outcome is recorded. */
export const CONVERSATION_OUTCOME_SCORE_NAME = "conversation_outcome";

/**
 * The v1 outcome taxonomy. Both values are ground truth, not inference:
 * - `contained`: the conversation concluded with NO operator reply, ever.
 * - `escalated`: a human operator replied at some point (`Message.type = HUMAN
 *   AND userId IS NOT NULL`) — the durable, path-agnostic truth of a handoff.
 *
 * They are disjoint and exhaustive over classified conversations, and a partial
 * unique index admits at most one outcome row per chat. That is precisely what
 * lets the funnel's two bars be read as parts of one whole: they sum to the
 * number of conversations classified in the period, with no double-counting.
 *
 * `resolved` vs `abandoned` is deliberately NOT split here — that partition
 * needs a heuristic (last-message direction / CSAT), so it is a future additive
 * value, not a v1 claim the data can support.
 */
export const CONVERSATION_OUTCOME_CONTAINED = "contained";
export const CONVERSATION_OUTCOME_ESCALATED = "escalated";

export const CONVERSATION_OUTCOMES = [
  CONVERSATION_OUTCOME_CONTAINED,
  CONVERSATION_OUTCOME_ESCALATED
] as const;

export type ConversationOutcome = (typeof CONVERSATION_OUTCOMES)[number];
